import { useState, useEffect, createContext, useContext } from 'react';

const LanguageContext = createContext();

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('ar');
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);

  // Auto-detect language from browser
  useEffect(() => {
    const browserLang = navigator.language || navigator.languages[0];
    const detectedLang = browserLang.startsWith('ar') ? 'ar' : 'en';
    const savedLang = localStorage.getItem('app_language') || detectedLang;
    setLanguage(savedLang);
  }, []);

  // Load translations
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const response = await fetch(`/locales/${language}.json`);
        const data = await response.json();
        setTranslations(data);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load translations:', error);
        setLoading(false);
      }
    };

    loadTranslations();
  }, [language]);

  // Update HTML attributes for RTL support
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem('app_language', language);
  }, [language]);

  const t = (path) => {
    const keys = path.split('.');
    let value = translations;
    
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return path;
    }
    
    return value || path;
  };

  const changeLanguage = (newLang) => {
    setLoading(true);
    setLanguage(newLang);
  };

  const isRTL = language === 'ar';

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      t, 
      isRTL,
      loading 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};